<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <!-- === New Publicitygroup === -->
      <b-card>
        <div slot="header">
          <h1>Gruppen - Neue Gruppe erstellen</h1>
          <div class="card-header-actions"></div>
        </div>
        <b-row>
          <b-col lg="8">
            <b-form
              v-on:submit.prevent="onSubmit"
              v-on:reset.prevent="onReset"
              novalidate
            >
              <!-- === Name of Publicitygroup === -->
              <b-form-group
                id="publicitygroupInputGroup1"
                label="Name der Gruppe *"
                label-for="publicitygroupname"
              >
                <b-form-input
                  id="publicitygroupname"
                  type="text"
                  aria-describedby="input1LiveFeedback1"
                  placeholder="Name der Gruppe"
                  autocomplete="publicitygroupname"
                  autofocus
                  v-model.trim="$v.form.publicitygroupname.$model"
                  :state="chkState('publicitygroupname')"
                />
                <b-form-invalid-feedback id="input1LiveFeedback1">
                  Dieses Feld muss angegeben werden und der Name der
                  Liegenschaft muss mindestens 2 Zeichen lang sein.
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- === Facilities of Publicitygroup === -->
              <b-form-group
                id="residentInputGroup2"
                label="Liegenschaften"
                label-for="residentfacility"
              >
                <!-- requires track-by prop as we are dealing with objects as elements of the selector -->
                <multiselect
                  id="publicitygroupfacilities"
                  v-model="publicitygroupfacilities"
                  label="name"
                  track-by="value"
                  :options="
                    getAssignedFacilities.map((facility) => {
                      return {
                        name: `${facility.street} ${facility.housenumber}, ${facility.Location}, Monitore: ${facility.numberofmonitors}`,
                        value: facility,
                      };
                    })
                  "
                  :multiple="true"
                  placeholder="Wählen Sie die gewünschte Liegenschaft aus"
                ></multiselect>
              </b-form-group>

              <!-- === Buttons === -->
              <b-button
                type="submit"
                variant="primary"
                :disabled="$v.form.$invalid || submitted || licenseExpired"
              >
                Erstellen
              </b-button>
              <b-button
                class="ml-1"
                type="reset"
                variant="danger"
                :disabled="!isDirty"
                >Verwerfen</b-button
              >
              <hr />
              <p class="req-field">* Pflichtfeld</p>
            </b-form>
          </b-col>
        </b-row>
      </b-card>

      <!-- === Publicitygroups Overview === -->
      <b-card header-tag="header" footer-tag="footer">
        <div slot="header">
          <h1>Gruppen - Übersicht</h1>
          <div class="card-header-actions"></div>
        </div>
        <b-card-body>
          <v-client-table
            v-if="getPublicitygroupStatus"
            :columns="columns"
            :data="getPublicitygroupOverview"
            :options="options"
            :theme="theme"
            id="dataTablePublicitygroups"
            ref="publicitygroupOverviewTable"
          >
            <!-- FACILITIES -->
            <div slot="facilities" slot-scope="{ row }">
              {{
                getPublicitygroupFacilities(row.groupname)
                  .map((facility) => {
                    return `${facility.street} ${facility.housenumber}`;
                  })
                  .toString()
                  .split(",")
                  .join(", ")
              }}
            </div>
            <div slot="settings" slot-scope="props">
              <b-dropdown variant="link" size="lg" no-caret>
                <template slot="button-content">
                  &#x2699;<span class="sr-only">Search</span>
                </template>

                <b-dropdown-item
                  :disabled="props.row.groupname === 'Alle'"
                  @click="gotoRoute(props.row.groupname)"
                  >Details/Bearbeiten</b-dropdown-item
                >
              </b-dropdown>
            </div>
          </v-client-table>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { ClientTable, Event } from "vue-tables-2";
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";

// VUE izitoast
import VueIziToast from "vue-izitoast";
import "izitoast/dist/css/iziToast.min.css";
Vue.use(VueIziToast);

import NotificationMixin from "../../../mixins/notificationmixin";

Vue.use(ClientTable);

const formShape = {
  publicitygroupname: "",
};

export default {
  name: "Publicitygroups",
  computed: {
    ...mapGetters([
      "getPublicitygroupStatus",
      "getPublicitygroupOverview",
      "getAssignedFacilityStatus",
      "getPublicitygroupFacilities",
      "getPublicitygroupFacilityObjects",
      "getAssignedFacilities",
      "getPublicitygroupIdByName",
      "licenseExpired",
    ]),
    formStr() {
      return JSON.stringify(this.form, null, 4);
    },
    isValid() {
      return !this.$v.form.$anyError;
    },
    isDirty() {
      return this.$v.form.$anyDirty;
    },
  },
  methods: {
    ...mapActions([
      "loadPublicitygroups",
      "loadAssignedFacilities",
      "submitPublicitygroup",
    ]),
    onSubmit() {
      if (this.validate()) {
        this.$nextTick(async () => {
          this.submitted = true;
          this.feedBack = "info";

          // push facility to global state and store it in the database:#
          // map facility display names (names of multiselect element) to
          // object with 2 props: businessid, facilityid
          let err = await this.submitPublicitygroup({
            publicitygroupname: this.$v.form.publicitygroupname.$model,
            publicitygroupfacilities: this.publicitygroupfacilities.map(
              (facility) => {
                return {
                  businessid: facility.value.Business_id,
                  facilityid: facility.value.idFacility,
                };
              }
            ),
          });
          this.notify(
            err,
            `Die Gruppe wurde erfolgreich angelegt!`,
            `Die Gruppe konnte nicht angelegt werden!`,
            this.$toast
          );
          // on success, add new publicitygroup to facility objects in global storage
          if (!err) {
            let publicitygroupid = this.getPublicitygroupIdByName(
              this.form.publicitygroupname
            );
            // go over all assinged facilities and add the newly created publicitygroup to facilities in question
            for (let fac of this.getAssignedFacilities) {
              for (let facility of this.publicitygroupfacilities) {
                if (facility.value == fac) {
                  // ... add the group
                  fac.Publicitygroups.push({
                    Groupname: this.form.publicitygroupname,
                    idPublicitygroup: publicitygroupid,
                  });
                }
              }
            }
          }
        });
      }
    },
    onReset() {
      // Reset validation
      this.form = Object.assign({}, formShape);
      this.submitted = false;
      this.$nextTick(() => {
        this.$v.$reset();
        this.feedBack = "secondary";
        this.publicitygroupfacilities = [];
      });
    },
    gotoRoute(groupname) {
      this.$router.push({
        name: "Gruppen-Zuweisung",
        params: { groupname },
      });
    },
    chkState(val) {
      const field = this.$v.form[val];
      if (field.$model === "") {
        return null;
      }
      return !field.$error;
    },
    findFirstError(component = this) {
      if (component.state === false) {
        if (component.$refs.input) {
          component.$refs.input.focus();
          return true;
        }
        if (component.$refs.check) {
          component.$refs.check.focus();
          return true;
        }
      }
      let focused = false;
      component.$children.some((child) => {
        focused = this.findFirstError(child);
        return focused;
      });

      return focused;
    },
    validate() {
      this.$v.$touch();
      this.$nextTick(() => this.findFirstError());
      return this.isValid;
    },
  },
  mounted: function () {
    if (!this.getAssignedFacilityStatus) {
      this.loadAssignedFacilities();
    }

    if (!this.getPublicitygroupStatus) {
      this.loadPublicitygroups();
    }
  },
  mixins: [validationMixin, NotificationMixin],
  validations: {
    form: {
      publicitygroupname: {
        required,
        minLength: minLength(2),
      },
    },
  },
  data() {
    return {
      // temporary removal of settings col
      columns: [
        "groupname",
        "numberOfFacilities",
        "facilities",
        "numberOfMonitors",
        "settings",
      ],
      options: {
        headings: {
          groupname: "Gruppenname",
          numberOfFacilities: "Anzahl der Liegenschaften",
          facilities: "Liegenschaften",
          numberOfMonitors: "Anzahl der Monitore",
          settings: "Einstellungen",
        },
        sortable: [
          "groupname",
          "numberOfFacilities",
          "facilities",
          "numberOfMonitors",
        ],
        filterable: [
          "groupname",
          "numberOfFacilities",
          "facilities",
          "numberOfMonitors",
        ],
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort",
        },
        pagination: {
          chunk: 5,
          edge: false,
          nav: "scroll",
        },
        texts: {
          filter: "Filter",
          filterPlaceholder: "Filtern nach ...",
          columns: "Spalten",
          limit: "Einträge",
          count: "Zeige Gruppen {from} bis {to} von {count} Einträgen",
        },
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default",
      form: Object.assign({}, formShape),
      feedBack: "secondary",
      submitted: false,
      publicitygroupfacilities: [],
    };
  },
  components: {
    ClientTable,
    Event,
    Multiselect,
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.req-field {
  font-size: 0.75em;
}
</style>

  